<template>
  <div class="historical-tvl">
    <template v-if="loading">
      Loading..
    </template>
    <template v-if="network">
      <div class="app-list-filters">
        <a-tag
          class="app-list-filters__field"
          v-for="item in timePeriods"
          :key="`time-period-${item.id}`"
          @click="() => setSelectedTimePeriod(item)"
          :class="item.id===selectedTimePeriod.id ? 'selected' : ''"
          :title="item.label"
        >
          {{ item.label }}
        </a-tag>

        <date-field
          class="app-list-filters__field"
          v-model="filters.createdAtFrom"
          :label="'logs-list.lbl-start-date' | globalize"
          :enable-time="false"
        />
        <date-field
          class="app-list-filters__field"
          v-model="filters.createdAtTo"
          :label="'logs-list.lbl-end-date' | globalize"
          :enable-time="false"
        />
      </div>
      <a-select
        mode="tags"
        :default-value="filters.symbol"
        :value="filters.symbol"
        class="custom-input"
        style="width: 80%"
        :placeholder="getTokenPlaceholder()"
        @change="setSelectedSymbols"
      >
        <a-select-option
          value="unselect"
          key="unselect">
          {{ 'historical-tvl.unselect-all' | globalize }}
        </a-select-option>
        <a-select-option
          value="selectAll"
          key="selectAll">
          {{ 'historical-tvl.select-all' | globalize }}
        </a-select-option>

        <a-select-option
          v-for="(opt) in symbols"
          :value="opt"
          :key="`${opt}`">
          <span
            :style="tagStyle(opt)">
            {{ opt }}
          </span>
        </a-select-option>
      </a-select>
      <template v-if="tvl.length > 0">
        <g-chart
          type="ComboChart"
          :settings="{ packages: ['corechart'] }"
          :options="options"
          :resize-debounce="500"
          :data="chart"
        />
      </template>
      <template v-else>
        <a-empty :description="'historical-tvl.empty-data' | globalize" />
      </template>
      <a-collapse
        expand-icon-position="right"
      >
        <a-collapse-panel
          key="1"
          :header="'historical-tvl.table-data' | globalize">
          <a-table
            v-if="filters.symbol.length > 0"
            size="small"
            :data-source="tvl"
            :row-key="(record, i)=>`${i}-${record.id}`"
            :pagination="true"
            :loading="loading"
            :row-class-name="(_record, index) => (index % 2 === 1 ? 'ant-table-striped' : null)"
            :columns="columns">
            <template
              slot="ethAddress-column"
              slot-scope="ethAddress,item">
              <div class="verification-details-photos__img-wrp">
                <a
                  :href="getTokenExplorerUrl(item)"
                  target="_blank">
                  <a-avatar
                    :src="item.iconUrl"
                  />
                  <span class="token-symbol">{{ item.symbol }}</span>
                </a>
              </div>
            </template>

            <template
              slot="usdValue-column"
              slot-scope="usdValue">
              <copyable :copy-text="usdValue">
                {{ usdValue }}
              </copyable>
            </template>

            <template
              slot="createdAt-column"
              slot-scope="createdAt">
              {{ createdAt | formatDateDMYT }}
            </template>
          </a-table>
        </a-collapse-panel>
      </a-collapse>
    </template>
  </div>
</template>

<script>
import TvlMixin from '@/vue/mixins/tvl.mixin'
import ATable from 'ant-design-vue/lib/table'
import ASelect from 'ant-design-vue/lib/select'
import ACollapse from 'ant-design-vue/lib/collapse/Collapse'
import ACollapsePanel from 'ant-design-vue/lib/collapse/CollapsePanel'
import DateField from '@/vue/fields/DateField'
import { GChart } from 'vue-google-charts/legacy'
import ATag from 'ant-design-vue/lib/tag'
import { api } from '@/api'
import { ErrorHandler } from '@/js/helpers/error-handler'
import moment from 'moment'
import { tokenIconDeliver } from '@/js/token-icons'
import Copyable from '@/vue/common/copyable/Copyable'
import { globalize } from '@/vue/filters/globalize'
import AAvatar from 'ant-design-vue/lib/avatar'
import { clearObject } from '@/js/helpers/clear-object'
import { groupBy } from '@/js/helpers/array'
import AEmpty from 'ant-design-vue/lib/empty'

const timePeriods = [
  {
    id: 'daily',
    label: 'Daily',
    startOf: 'day',
    diffKey: 'days',
  },
  {
    id: 'weekly',
    label: 'Weekly',
    startOf: 'week',
    diffKey: 'weeks',
  },
  {
    id: 'monthly',
    label: 'Monthly',
    startOf: 'month',
    diffKey: 'months',
  },
  {
    id: 'yearly',
    label: 'Yearly',
    startOf: 'year',
    diffKey: 'years',
  },
]

const columns = ctx => {
  /* eslint-disable-next-line */
  const h = ctx.$createElement
  return [
    {
      dataIndex: 'ethAddress',
      key: 'ethAddress',
      width: 100,
      title: globalize('historical-tvl.lbl-ethAddress'),
      scopedSlots: { customRender: 'ethAddress-column' },
    },
    {
      dataIndex: 'decimals',
      width: 100,
      key: 'decimals',
      title: globalize('historical-tvl.lbl-decimals'),
    },
    {
      dataIndex: 'usdValue',
      key: 'usdValue',
      width: 200,
      title: globalize('historical-tvl.lbl-usdValue'),
      defaultSortOrder: 'descend',
      sorter: (a, b) => {
        return b.usdValue - a.usdValue
      },
      scopedSlots: { customRender: 'usdValue-column' },
    },
    {
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 200,
      title: globalize('historical-tvl.lbl-createdAt'),
      defaultSortOrder: 'descend',
      sorter: (a, b) => {
        return new Date(b.createdAt) - new Date(a.createdAt)
      },
      scopedSlots: { customRender: 'createdAt-column' },
    },
  ]
}

export default {
  name: 'historical-tvl',
  components: {
    'a-collapse': ACollapse,
    'a-collapse-panel': ACollapsePanel,
    GChart,
    ATag,
    DateField,
    ATable,
    Copyable,
    AAvatar,
    ASelect,
    'a-select-option': ASelect.Option,
    AEmpty,
  },

  mixins: [TvlMixin],

  data () {
    return {
      ...this.data,
      options: {
        // gchart options description:
        //  https://developers.google.com/chart/interactive/docs/gallery/linechart?hl=en
        legend: { position: 'none' },
        width: 1200,
        height: 400,
        pointSize: 5,
        lineWidth: 4,
        displayAnnotations: true,
        enableInteractivity: true,
        interpolateNulls: true,
        series: [],
        hAxis: {
          title: globalize('historical-tvl.date'),
          textStyle: {
            fontSize: 12,
          },
          gridlines: { interval: 1 },
          viewWindowMode: 'maximized',
        },
        vAxis: {
          title: globalize('historical-tvl.usd'),
          scaleType: 'log',
          // gridlines: { count: 4 },
          format: 'short',
        },
        allowRedraw: true,
        // curveType: 'function',
        intervals: { 'style': 'line' },
        axisTitlesPosition: 'out',
      },
      symbols: [],
      symbolColors: {},
      selectedTimePeriod: timePeriods[0],
      timePeriods,
      filters: {
        chainId: this.network.chainId,
        type: null,
        createdAtFrom: moment(new Date()).subtract(7, 'days').toDate().toISOString(),
        createdAtTo: moment(new Date()).toDate().toISOString(),
        symbol: [],
        groupBy: null,
      },
    }
  },

  computed: {
    columns () {
      const result = columns(this)

      return result
    },
    chart () {
      const result = this.convertToChartData()
      return result
    },
  },
  async created () {
    this.loading = true

    await this.getTvls()
    this.symbols = this.getSymbols(this.tvl)

    this.filters.symbol = this.symbols

    this.filters.symbol.forEach((sym, i) => {
      this.symbolColors[sym] = this.colors[i]
    })

    this.fillGChartSeries(this.filters.symbol)
    this.loading = false
  },
  methods: {
    getTokenPlaceholder () {
      if (this.isFilterSymbolsEmpty()) {
        return globalize('historical-tvl.only-total-values')
      }
      return ''
    },

    getSymbols (tvls) {
      const m = {}
      tvls.forEach(tvl => {
        m[tvl.symbol] = 1
      })

      return Object.keys(m).sort((a, b) => {
        if (a < b) {
          return -1
        }
        if (a > b) {
          return 1
        }
        return 0
      })
    },

    async getTvls () {
      try {
        const query = clearObject({
          'filter[type]': this.tvlType,
          'filter[chain_id]': this.network.chainId,
          'filter[date_before]': new Date(this.filters.createdAtFrom).toISOString(),
          'filter[date_after]': new Date(this.filters.createdAtTo).toISOString(),
          'filter[symbol]': this.filters.symbol,
          'page[group_by]': this.filters.groupBy !== 'day' ? this.filters.groupBy : null,
          'page[limit]': 10000,
        })
        const response = await api.getWithSignature(`/tvl/historical`, query)

        const tvl = response.data && response.data.forEach ? response.data : []

        tvl.forEach(record => {
          record.iconUrl = tokenIconDeliver.getIconUrl(record.symbol)
          record.price = record.usdPrice
        })
        // show only with usd > 0
        this.tvl = tvl.filter(r => +r.usdValue > 0)
      } catch (e) {
        ErrorHandler.processWithoutFeedback(e)
      }
    },
    convertToChartData () {
      const isFilterSymbolsEmpty = this.isFilterSymbolsEmpty()

      const tokens = isFilterSymbolsEmpty ? this.symbols : this.filters.symbol
      let chart = [
        [
          this.selectedTimePeriod.label,
        ],
      ]
      !isFilterSymbolsEmpty && chart[0].push(...this.filters.symbol)

      chart[0].push(globalize('historical-tvl.total-values'))

      const grouped = groupBy(this.tvl, 'createdAt')
      const sortedDates = Object.keys(grouped).sort((a, b) => {
        return new Date(a) - new Date(b)
      })

      sortedDates.forEach(dateKey => {
        const row = [
          new Date(dateKey),
        ]
        let totalValue = 0

        tokens.forEach(token => {
          const found = grouped[dateKey].find(tvl => {
            return tvl.symbol === token && tvl.createdAt === dateKey
          })
          let calcValue = 0
          if (found) {
            calcValue = +found.usdValue
            totalValue = totalValue + (+found.usdValue)
          }
          !isFilterSymbolsEmpty && row.push(calcValue)
        }) // END tokens.forEach(token => {
        row.push(totalValue)

        chart.push(row)
      })

      const maxValue = this.getMaxUsdValue()
      this.options.vAxis.maxValue = maxValue.toString()
      this.options.hAxis.minValue = new Date(this.filters.createdAtFrom)
      this.options.hAxis.maxValue = new Date(this.filters.createdAtTo)

      this.fillGChartSeries(tokens)
      this.options.series.push({ color: this.colors[this.colors.length - 1] })

      switch (this.selectedTimePeriod.id) {
        case this.timePeriods[0].id:
        case this.timePeriods[1].id:
          this.options.hAxis.format = 'MM-dd'
          break
        case this.timePeriods[2].id:
          this.options.hAxis.format = 'YY-MM-dd'
          break
        case this.timePeriods[3].id:
          this.options.hAxis.format = 'YYYY'
          break
      }

      // chart data example
      // const chart = [
      //   ['Month', 'SMT', 'USDC', 'WBTC', 'WETH'],
      //   ['Jan', 1000, 200, 200, 2000],
      // ]
      return chart
    },

    setSelectedTimePeriod (val) {
      this.selectedTimePeriod = val
      this.filters.groupBy = val.startOf
    },

    isFilterSymbolsEmpty () {
      return this.filters.symbol.length === 0
    },

    fillGChartSeries (symbols) {
      this.options.series = []
      // series: {5: {type: 'line'}}
      symbols.forEach((token, i) => {
        this.options.series.push({ color: this.symbolColors[token] })
      })
    },
  },
}
</script>

<style scoped lang="scss">
  @import '~@scss/variables';
  @import '~ant-design-vue/lib/divider/style/index.css';
  @import '~ant-design-vue/lib/tag/style/index.css';
  @import '~ant-design-vue/lib/avatar/style/index.css';
  @import '~ant-design-vue/lib/select/style/index.css';
  @import '~ant-design-vue/lib/empty/style/index.css';

  .historical-tvl {
    h3 {
      margin-bottom: 1em;
    }

    .app-list-filters {
      box-shadow: none;
    }

    .selected {
      background: $_lightGreen;
    }

    .ant-tag {
      cursor: pointer;
    }

    .token-symbol {
      margin-left: 1em;
    }
  }
</style>
